import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useGetApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setSnapshotData } from "src/redux/slices/generatorSlice";
import { setUsers } from "src/redux/slices/entriesGeneratorSlice";
import { getYachtId } from "src/utils";
import { hasFlag } from "src/utils/flagUtils";
import {
  getWidgetHeaders,
  getWidgetRows,
} from "src/components/pages/Home/CustomDashboard/Widget.utils";

/**
 * Hook to fetch historical snapshot data when showSnapshotEntry flag is present
 */
export const useFetchSnapshot = () => {
  const { logbookId } = useParams();
  const dispatch = useDispatch();
  const { flags, users: generatorUsers, snapshotPageSize } = useSelector(
    state => state.generatorSlice,
  );

  const shouldFetchSnapshot = hasFlag(flags, "showSnapshotEntry");

  // Fetch snapshot data from get-preview endpoint
  const { data: snapshotData, refetch } = useGetApi<any>({
    request: "get-preview",
    body: {
      params: {
        lbid: logbookId,
        yachtid: getYachtId(),
        page: 1,
        pageMax: snapshotPageSize,
      },
    },
    fetchOnInit: false,
  });

  // Trigger snapshot fetch when flag is detected
  useEffect(() => {
    if (!shouldFetchSnapshot) return;
    refetch();
  }, [shouldFetchSnapshot, refetch, snapshotPageSize]);

  // Process and store snapshot data when available
  useEffect(() => {
    if (shouldFetchSnapshot && snapshotData?.success && snapshotData.list) {
      const headers = ["Watchkeeper", "Events"].includes(
        snapshotData.lbname ?? "",
      )
        ? getWidgetHeaders(snapshotData.headers, true)
        : snapshotData.headers;

      const list =
        snapshotData.list.length > 0 &&
        typeof snapshotData?.list[0] === "object" &&
        "version" in snapshotData?.list[0]
          ? getWidgetRows(snapshotData.list)
          : snapshotData.list;

      dispatch(
        setSnapshotData({
          snapshotHeaders: headers,
          snapshotEntry: {
            list,
            lbname: snapshotData.lbname,
          },
          flags,
        }),
      );

      // ValueRenderer uses entriesGeneratorSlice.users, so ensure it's populated
      // for user/ref columns inside SnapshotDisplay.
      const snapshotUsers = snapshotData?.users ?? generatorUsers ?? [];
      if (Array.isArray(snapshotUsers) && snapshotUsers.length > 0) {
        dispatch(setUsers(snapshotUsers));
      }
    }
  }, [snapshotData, shouldFetchSnapshot, dispatch, flags, generatorUsers]);

  return null;
};
